/**
 * How It Works marketing page (`/how-it-works`) — GitHub issue "Create
 * React components for static marketing pages using vite-react-ssg".
 * Statically generated; no client-side data fetching or auth dependency.
 */
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import MarketingLayout from '@/components/marketing/MarketingLayout';
import SeoHead from '@/components/marketing/SeoHead';
import { Button } from '@/components/ui/button';
import TalkToHeroWidget from '@/components/widget/TalkToHeroWidget';

const STEP_KEYS = ['one', 'two', 'three', 'four'] as const;

export default function HowItWorksPage(): JSX.Element {
  const { t } = useTranslation();

  return (
    <MarketingLayout>
      <SeoHead
        title={t('marketing.howItWorks.title')}
        description={t('marketing.howItWorks.description')}
        path="/how-it-works"
      />

      <section className="container mx-auto px-4 py-16 text-center">
        <h1 className="text-4xl font-bold tracking-tight sm:text-5xl">
          {t('marketing.howItWorks.heading')}
        </h1>
        <p className="mx-auto mt-4 max-w-2xl text-xl text-muted-foreground">
          {t('marketing.howItWorks.subheading')}
        </p>
        <div className="mt-6 flex justify-center">
          <TalkToHeroWidget />
        </div>
      </section>

      <section aria-label={t('marketing.howItWorks.heading')} className="border-t bg-muted/30">
        <div className="container mx-auto px-4 py-16">
          <ol className="mx-auto grid max-w-4xl gap-8 sm:grid-cols-2">
            {STEP_KEYS.map((key, index) => (
              <li key={key} className="rounded-lg border bg-card p-6">
                <span
                  aria-hidden="true"
                  className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-sm font-semibold text-primary-foreground"
                >
                  {index + 1}
                </span>
                <h2 className="mt-4 text-xl font-semibold">
                  {t(`marketing.howItWorks.steps.${key}.title`)}
                </h2>
                <p className="mt-2 text-muted-foreground">
                  {t(`marketing.howItWorks.steps.${key}.description`)}
                </p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="container mx-auto px-4 py-16 text-center">
        <Button size="lg" asChild>
          <Link to="/getstarted">{t('marketing.howItWorks.cta')}</Link>
        </Button>
      </section>
    </MarketingLayout>
  );
}
